// @ts-nocheck
// Persistent shell para o navegador-geofinanceiro do app-publico.
// SmartMap montado UMA VEZ; sidebar swaps via React Router <Outlet/>. Mapa
// nunca remonta na navegação, preservando zoom/pan/basemap/layer state
// entre seleções de lote/imovel (UX tipo Google Maps).
//
// Adaptado do app/src/components/Navegador/NavegadorShell.tsx:
// - Removido sessionInfo/hasPermission/areas-observação (não há auth no público)
// - identifyOnExtent vem das actions/Lote (não @/api/lote como no admin)
// - Search bar usa o componente Find existente (pages/Finance/Imovel/Find)
import { useCallback, useLayoutEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ActionIcon, Paper, Tooltip } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

import SmartMap, { mapKey } from "../Map/SmartMap";
import SmartMapLegend from "../Map/SmartMapLegend";
import Find from "../../pages/Finance/Imovel/Find";
import * as loteActions from "../../actions/Lote";
import { useAppDispatch } from "../../store/hooks";
import { NavegadorMapProvider, useNavegadorMap } from "./NavegadorMapContext";
import "./NavegadorShell.css";

export interface NavegadorShellProps {
  /** Base route — usado pra navegação (botão fechar, identify results). */
  baseRoute: string;
  /** mapKey passado pra SmartMap. */
  mapKeyValue?: string;
}

/** Sidebar visível quando: child route matched (lote/:id, imovel/:id) OU
 *  query params presentes (q, e). `page` sozinho é ignorado — paginação
 *  sem filtro não tem lista pra mostrar. */
function useShouldShowSidebar(baseRoute: string): boolean {
  const location = useLocation();
  // Normaliza trailing slash mas preserva "/" quando a rota é raiz.
  const trim = (s: string) => (s.length > 1 ? s.replace(/\/+$/, "") : s);
  const isExactBase = trim(location.pathname) === trim(baseRoute);
  const params = new URLSearchParams(location.search);
  const hasSearchParams = params.has("q") || params.has("e");
  return !isExactBase || hasSearchParams;
}

function NavegadorShellInner({ baseRoute, mapKeyValue }: NavegadorShellProps) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { geoJson, smartMapApiRef, setSelectedId } = useNavegadorMap();

  // Anchor do shell entre Breadcrumbs (top) e Footer (bottom). Computado
  // dinamicamente porque altura do header varia e um CSS calc estático erra.
  const shellRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const FOOTER_H = 22;
    const update = () => {
      const top = node.getBoundingClientRect().top;
      const h = Math.max(window.innerHeight - top - FOOTER_H, 240);
      node.style.height = `${h}px`;
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // Auto-stack dos controles do mapa na coluna bottom-right (top→bottom).
  // Pulado quando elemento ausente (mapConfig disabled) ou zero-height.
  // SmartMap cria esses async via Redux + dynamic config — observamos via MutationObserver.
  useLayoutEffect(() => {
    const node = shellRef.current;
    if (!node) return;
    const SELECTORS_TOP_TO_BOTTOM = [
      ".btn-map-info",
      ".map-custom-tools",
      ".btn-measure",
      ".ol-zoom",
    ];
    const GAP = 8;
    const BASE = 12;
    let scheduled = false;
    const arrange = () => {
      scheduled = false;
      let bottom = BASE;
      for (let i = SELECTORS_TOP_TO_BOTTOM.length - 1; i >= 0; i--) {
        const el = node.querySelector(SELECTORS_TOP_TO_BOTTOM[i]) as HTMLElement | null;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.height === 0) continue;
        el.style.setProperty("position", "absolute", "important");
        el.style.setProperty("bottom", `${bottom}px`, "important");
        el.style.setProperty("top", "auto", "important");
        bottom += rect.height + GAP;
      }
    };
    const scheduleArrange = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(arrange);
    };
    scheduleArrange();
    const obs = new MutationObserver(scheduleArrange);
    obs.observe(node, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleArrange);
    return () => {
      obs.disconnect();
      window.removeEventListener("resize", scheduleArrange);
    };
  }, []);

  const showSidebar = useShouldShowSidebar(baseRoute);
  const resolvedMapKey = mapKeyValue ?? mapKey.SMARTPUBLIC;

  // Click-to-identify no mapa. Single result → navigate to /lote/:id; múltiplos
  // → navigate to /navegador?e=<extent>. Mapa fica montado, só a sidebar swap.
  const handleIdentifyOnExtent = useCallback(
    (geographicExtent: number[]) => {
      const nid = notifications.show({
        loading: true,
        title: "Identificando lotes",
        message: "Consultando lotes na área selecionada...",
        autoClose: false,
        withCloseButton: false,
      });
      dispatch((loteActions as any).identifyOnExtent(
        geographicExtent,
        ({ data }: { data: number[] | null }) => {
          notifications.hide(nid);
          if (!data || data.length === 0) {
            notifications.show({ color: "yellow", message: "Nenhum lote encontrado na área." });
          } else if (data.length === 1) {
            navigate(`${baseRoute}/lote/${data[0]}`);
          } else {
            const e = JSON.stringify(geographicExtent).slice(1, -1);
            navigate(`${baseRoute}?e=${e}`);
          }
        },
      ));
    },
    [baseRoute, navigate, dispatch],
  );

  const handleCloseSidebar = () => {
    setSelectedId(null);
    navigate(baseRoute);
  };

  return (
    <div ref={shellRef} className="navegador-shell">
      <div className="navegador-shell__map">
        <SmartMap
          mapKey={resolvedMapKey}
          onApiReady={(api: unknown) => {
            smartMapApiRef.current = api;
          }}
          geoJson={geoJson}
          identifyOnExtent={handleIdentifyOnExtent}
          controls={null}
        />
        <SmartMapLegend className="navegador-shell__legend" />
      </div>

      <div className="navegador-shell__search">
        <Paper shadow="md" radius="md" withBorder p={4}>
          <Find />
        </Paper>
      </div>

      {showSidebar && (
        <Paper shadow="md" radius="md" className="navegador-shell__sidebar" withBorder>
          <div className="navegador-shell__sidebar-close">
            <Tooltip label="Fechar e voltar ao mapa">
              <ActionIcon variant="subtle" onClick={handleCloseSidebar} aria-label="Fechar sidebar">
                <IconX size={18} />
              </ActionIcon>
            </Tooltip>
          </div>
          <div className="navegador-shell__sidebar-body">
            <Outlet />
          </div>
        </Paper>
      )}
    </div>
  );
}

export default function NavegadorShell(props: NavegadorShellProps) {
  return (
    <NavegadorMapProvider>
      <NavegadorShellInner {...props} />
    </NavegadorMapProvider>
  );
}
